/** Inferência de template a partir do título do slide (+ helpers de layout CCO-531). */

export type CabSlideTemplate =
  | 'capa'
  | 'fields'
  | 'telas'
  | 'riscos_impactos'
  | 'conclusao'
  | 'brand_closing'
  | 'elenco'
  | 'somos_gptw';

export type CabBrandTemplate = 'brand_closing' | 'elenco' | 'somos_gptw';

export type CabRiscosRow = {
  risco: string;
  status: string;
  comentarios: string;
};

export type CabConclusaoLayout = {
  template: 'conclusao';
  subtitle?: string;
  phases: string[];
  dates: string[];
  active: boolean[][];
  star_phase_index?: number;
  star_date_index?: number;
};

export type CabRiscosLayout = {
  template: 'riscos_impactos';
  subtitle?: string;
  rows: CabRiscosRow[];
};

export type CabBrandLayout = {
  template: CabBrandTemplate;
  backgroundImage?: string;
};

export type CabSlideLayout = CabConclusaoLayout | CabRiscosLayout | CabBrandLayout | { template?: string };

export const CCO531_SUBTITLE =
  'CCO-531 - Implantar módulo de Gestão de Mudanças no SuportNect';

export const DEFAULT_CONCLUSAO_PHASES = [
  'Planejamento',
  'Documentação',
  'Construção',
  'Testes',
  'Homologação',
  'Go-Live',
  'Estabilização',
] as const;

export const DEFAULT_CONCLUSAO_DATES = [
  '21/08/2026',
  '24/08/2026',
  '17/09/2026',
  '21/09/2026',
  '25/09/2026',
  '01/10/2026',
  '01/11/2026',
] as const;

export const CAB_CCO_SEED_PREFIX = '/uploads/changes/cab-cco-seed';

export const BRAND_DEFAULT_IMAGES: Record<CabBrandTemplate, string> = {
  brand_closing: `${CAB_CCO_SEED_PREFIX}/cco531-fechamento.png`,
  elenco: `${CAB_CCO_SEED_PREFIX}/cco531-elenco.png`,
  somos_gptw: `${CAB_CCO_SEED_PREFIX}/cco531-somos-gptw.png`,
};

/** Migra /cab-cco e JPEG legado → seed autenticado (S3/disco). */
export function rewriteLegacyCabBrandImagePath(src: string): string {
  const value = String(src || '').trim();
  if (!value) return value;
  const legacy: Record<string, string> = {
    '/cab-cco/cco531-fechamento.png': BRAND_DEFAULT_IMAGES.brand_closing,
    '/cab-cco/cco531-elenco.png': BRAND_DEFAULT_IMAGES.elenco,
    '/cab-cco/cco531-elenco.jpg': BRAND_DEFAULT_IMAGES.elenco,
    '/cab-cco/cco531-somos-gptw.png': BRAND_DEFAULT_IMAGES.somos_gptw,
    '/cab-cco/cco531-somos-gptw.jpg': BRAND_DEFAULT_IMAGES.somos_gptw,
  };
  return legacy[value] || value;
}

export function resolveCabSlideTemplate(title?: string | null): CabSlideTemplate {
  const normalized = (title || '').trim().toLowerCase();
  if (!normalized) return 'fields';
  // Match por título canônico / prefixo — evita "capacidades" → capa, "mitigação de risco" → riscos.
  if (normalized === 'capa' || normalized.startsWith('capa ') || normalized.startsWith('capa da')) {
    return 'capa';
  }
  if (
    normalized === 'riscos e impactos' ||
    normalized.startsWith('riscos e impacto')
  ) {
    return 'riscos_impactos';
  }
  if (normalized === 'conclusão' || normalized === 'conclusao') return 'conclusao';
  if (normalized === 'fechamento petronect' || normalized.startsWith('fechamento petronect')) {
    return 'brand_closing';
  }
  if (normalized === 'elenco petronect' || normalized.startsWith('elenco petronect')) {
    return 'elenco';
  }
  if (normalized === 'somos gptw' || normalized.startsWith('somos gptw')) return 'somos_gptw';
  if (normalized === 'telas' || normalized.startsWith('telas ')) return 'telas';
  return 'fields';
}

export function isBrandCabTemplate(template: CabSlideTemplate): template is CabBrandTemplate {
  return template === 'brand_closing' || template === 'elenco' || template === 'somos_gptw';
}

export function isStructuredCabTemplate(template: CabSlideTemplate): boolean {
  return (
    template === 'riscos_impactos' ||
    template === 'conclusao' ||
    isBrandCabTemplate(template)
  );
}

const KNOWN_CAB_SLIDE_TEMPLATES: readonly CabSlideTemplate[] = [
  'capa',
  'fields',
  'telas',
  'riscos_impactos',
  'conclusao',
  'brand_closing',
  'elenco',
  'somos_gptw',
] as const;

/** Template explícito no layout (permite título livre sem perder o tipo). */
export function templateFromLayout(
  layout?: CabSlideLayout | Record<string, unknown> | null,
): CabSlideTemplate | null {
  if (!layout || typeof layout !== 'object') return null;
  const raw = (layout as { template?: unknown }).template;
  if (typeof raw !== 'string') return null;
  const value = raw.trim() as CabSlideTemplate;
  return KNOWN_CAB_SLIDE_TEMPLATES.includes(value) ? value : null;
}

/** Tipos oferecidos ao criar um slide no editor. */
export type CabNewSlideKind = 'livre' | 'imagem' | 'conclusao' | 'riscos' | 'capa';

export type CabNewSlideOption = {
  kind: CabNewSlideKind;
  label: string;
  description: string;
};

export const CAB_NEW_SLIDE_OPTIONS: CabNewSlideOption[] = [
  {
    kind: 'livre',
    label: 'Livre (faixa azul)',
    description:
      'Faixa azul à esquerda e área à direita livre para rótulos, campos, textos e imagens.',
  },
  {
    kind: 'imagem',
    label: 'Só imagem (tela cheia)',
    description: 'Slide full-bleed só com arte de fundo — sem faixa azul.',
  },
  {
    kind: 'conclusao',
    label: 'Datas e fases',
    description: 'Grade com datas, fases e estrelinha — dá para ajustar quantidades e posição.',
  },
  {
    kind: 'riscos',
    label: 'Riscos e impactos',
    description: 'Tabela de riscos com linhas editáveis.',
  },
  {
    kind: 'capa',
    label: 'Capa',
    description: 'Capa com logo, título e data da reunião.',
  },
];

export function buildNewCabDeckSlide(
  kind: CabNewSlideKind,
  index: number,
): {
  title: string;
  sort_order: number;
  is_active: boolean;
  elements: [];
  layout: CabSlideLayout;
} {
  const n = index + 1;
  if (kind === 'imagem') {
    return {
      title: `Imagem ${n}`,
      sort_order: index,
      is_active: true,
      elements: [],
      layout: defaultBrandLayout('somos_gptw'),
    };
  }
  if (kind === 'conclusao') {
    return {
      title: `Datas e fases ${n}`,
      sort_order: index,
      is_active: true,
      elements: [],
      layout: defaultConclusaoLayout(),
    };
  }
  if (kind === 'riscos') {
    return {
      title: `Riscos e impactos ${n}`,
      sort_order: index,
      is_active: true,
      elements: [],
      layout: defaultRiscosLayout(),
    };
  }
  if (kind === 'capa') {
    return {
      title: n === 1 ? 'Capa da mudança' : `Capa ${n}`,
      sort_order: index,
      is_active: true,
      elements: [],
      layout: { template: 'capa' },
    };
  }
  return {
    title: `Novo slide ${n}`,
    sort_order: index,
    is_active: true,
    elements: [],
    layout: { template: 'fields' },
  };
}

export function defaultConclusaoLayout(): CabConclusaoLayout {
  const n = DEFAULT_CONCLUSAO_DATES.length;
  const active = DEFAULT_CONCLUSAO_PHASES.map((_, i) =>
    Array.from({ length: n }, (__, j) => i === j),
  );
  return {
    template: 'conclusao',
    subtitle: CCO531_SUBTITLE,
    phases: [...DEFAULT_CONCLUSAO_PHASES],
    dates: [...DEFAULT_CONCLUSAO_DATES],
    active,
    star_phase_index: 2,
    star_date_index: 2,
  };
}

export function defaultRiscosLayout(): CabRiscosLayout {
  return {
    template: 'riscos_impactos',
    subtitle: CCO531_SUBTITLE,
    rows: [
      { risco: '', status: '', comentarios: '' },
      { risco: '', status: '', comentarios: '' },
      { risco: '', status: '', comentarios: '' },
      { risco: '', status: '', comentarios: '' },
    ],
  };
}

export function defaultBrandLayout(kind: CabBrandTemplate): CabBrandLayout {
  return {
    template: kind,
    backgroundImage: BRAND_DEFAULT_IMAGES[kind],
  };
}

export function defaultLayoutForTemplate(template: CabSlideTemplate): CabSlideLayout | null {
  if (template === 'conclusao') return defaultConclusaoLayout();
  if (template === 'riscos_impactos') return defaultRiscosLayout();
  if (isBrandCabTemplate(template)) return defaultBrandLayout(template);
  return null;
}

/** Mescla layout persistido com defaults do template (layout.template tem prioridade sobre o título). */
export function resolveSlideLayout(
  title?: string | null,
  layout?: CabSlideLayout | Record<string, unknown> | null,
): { template: CabSlideTemplate; layout: CabSlideLayout | null } {
  const template = templateFromLayout(layout) || resolveCabSlideTemplate(title);
  const defaults = defaultLayoutForTemplate(template);
  if (!defaults) {
    return { template, layout: (layout as CabSlideLayout) || (template === 'capa' || template === 'fields' || template === 'telas' ? { template } : null) };
  }
  const raw = (layout || {}) as Record<string, unknown>;
  if (template === 'conclusao') {
    const base = defaultConclusaoLayout();
    const phases = Array.isArray(raw.phases)
      ? (raw.phases as unknown[]).map((x) => String(x || '')).slice(0, 20)
      : base.phases;
    const dates = Array.isArray(raw.dates)
      ? (raw.dates as unknown[]).map((x) => String(x || '')).slice(0, 20)
      : base.dates;
    const active = Array.isArray(raw.active)
      ? (raw.active as unknown[]).map((row) =>
          Array.isArray(row) ? row.map((c) => Boolean(c)).slice(0, 20) : [],
        )
      : base.active;
    return {
      template,
      layout: {
        template: 'conclusao',
        subtitle: String(raw.subtitle ?? base.subtitle ?? ''),
        phases,
        dates,
        active,
        star_phase_index:
          typeof raw.star_phase_index === 'number' ? raw.star_phase_index : base.star_phase_index,
        star_date_index:
          typeof raw.star_date_index === 'number' ? raw.star_date_index : base.star_date_index,
      },
    };
  }
  if (template === 'riscos_impactos') {
    const base = defaultRiscosLayout();
    const rows = Array.isArray(raw.rows)
      ? (raw.rows as unknown[])
          .filter((r): r is Record<string, unknown> => Boolean(r) && typeof r === 'object')
          .map((r) => ({
            risco: String(r.risco || ''),
            status: String(r.status || ''),
            comentarios: String(r.comentarios || ''),
          }))
          .slice(0, 30)
      : base.rows;
    while (rows.length < 4) {
      rows.push({ risco: '', status: '', comentarios: '' });
    }
    return {
      template,
      layout: {
        template: 'riscos_impactos',
        subtitle: String(raw.subtitle ?? base.subtitle ?? ''),
        rows,
      },
    };
  }
  if (isBrandCabTemplate(template)) {
    const base = defaultBrandLayout(template);
    const img = typeof raw.backgroundImage === 'string' ? raw.backgroundImage.trim() : '';
    // Migra /cab-cco legado → seed autenticado.
    const normalizedImg = rewriteLegacyCabBrandImagePath(img);
    return {
      template,
      layout: {
        template,
        backgroundImage: normalizedImg || base.backgroundImage,
      },
    };
  }
  return { template, layout: defaults };
}

export function formatCabSlideDate(value?: string | Date | null): string {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return new Date().toLocaleDateString('pt-BR');
  }
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function labelForCabField(
  key: string,
  catalog: Array<{ key: string; label: string }>,
): string {
  return catalog.find((item) => item.key === key)?.label || key.replace(/_/g, ' ');
}
